import { ProxyScribeClient as ScribeClient } from "@el8/scribe-client/dist/proxy-client";
import {
  type IBlobEvent,
  type IMediaRecorder,
  MediaRecorder,
} from "extendable-media-recorder";
import { useEffect } from "react";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import {
  IS_CHROME_EXTENSION,
  NABLA_RECOMMENDED_RECORDING_SAMPLE_RATE_HZ,
  SCRIBE_RECORDER_TIMESLICE_MS,
} from "../constants";
import useEffectOnce from "../hooks/useEffectOnce";
import usePrevious from "../hooks/usePrevious";
import { scribeClientLogger } from "../logger";
import {
  type AiScribeStep,
  NablaAPILanguage,
  type NablaAPILanguage as ScribeLanguage,
} from "../types";
import { doesBrowserSupportDownsamplingAudio } from "../utils/browser";
import storage, { chromeStorage } from "../utils/localStorage";
import {
  resolveScribePipelineAction,
  resolveScribeTeardownAction,
} from "../utils/scribeLifecycle";
import { isScribeReady } from "../utils/scribeReady";

function createAudioContext(): AudioContext {
  return new AudioContext({
    sampleRate: doesBrowserSupportDownsamplingAudio()
      ? NABLA_RECOMMENDED_RECORDING_SAMPLE_RATE_HZ
      : undefined,
  });
}

interface ScribeSetupOptions {
  getAuthToken: () => Promise<string>;
  websocketUrl: string;
}

function createScribeClient(
  options: ScribeSetupOptions,
  sampleRate: number,
): ScribeClient {
  return new ScribeClient({
    audio: {
      encoding: "pcm_s16le",
      sampleRate,
      timeslice: SCRIBE_RECORDER_TIMESLICE_MS,
    },
    auth: async () => {
      const authToken = await options.getAuthToken();

      console.log("authToken", authToken);
      return authToken;
    },
    drainStrategy: "speed-limit",
    // @ts-expect-error: scribe client logger levels vs @datadog/browser-logs
    logger: scribeClientLogger,
    target: options.websocketUrl,
    transport: "el8-transcribe",
  });
}

export interface ScribeStore {
  step: AiScribeStep;
  setStep: (step: AiScribeStep) => void;
  language: ScribeLanguage;
  setLanguage: (language: ScribeLanguage) => void;
  setMicrophoneDeviceId: (microphoneDeviceId: string | null) => void;
  getMediaDevices: () => Promise<MediaDeviceInfo[]>;
  setupMicrophoneTest: (
    microphoneDeviceId?: string,
    isCancelled?: () => boolean,
  ) => Promise<MediaStream | null>;
  setup: (
    options: ScribeSetupOptions,
    isCancelled?: () => boolean,
  ) => Promise<void>;
  /**
   * Disposes the scribe client and media pipeline. `mode` defaults to "hard"
   * (terminate the worker). Pass "graceful" to flush buffered audio and let
   * el8-transcribe finalize the session — required when disposing a live
   * recording, since a hard terminate kills the worker and closes the socket
   * with code 1001, dropping any un-flushed audio.
   */
  teardown: (mode?: "hard" | "graceful") => void;
  media: {
    mediaStream: MediaStream | null;
    microphoneDeviceId: string | null;
    mediaDevices: MediaDeviceInfo[] | null;
    audioContext: AudioContext;
    audioSource: MediaStreamAudioSourceNode | null;
    audioDestination: MediaStreamAudioDestinationNode | null;
    mediaRecorder: IMediaRecorder | null;
  };
  scribe: ScribeClient | null;
  scribingSessionId: string | undefined;
  setScribingSessionId: (scribingSessionId: string | undefined) => void;
}

export const scribeStore = create<ScribeStore>()(
  persist(
    (set, get) => ({
      step: "initial",
      language: NablaAPILanguage.English,
      media: {
        mediaStream: null,
        microphoneDeviceId: null,
        mediaDevices: null,
        audioContext: createAudioContext(),
        audioSource: null,
        audioDestination: null,
        mediaRecorder: null,
      },
      scribe: null,
      scribingSessionId: undefined,
      setScribingSessionId: (scribingSessionId) => {
        set({ scribingSessionId });
      },
      setup: async (options, isCancelled) => {
        const audioContext = createAudioContext();
        const microphoneDeviceId = get().media.microphoneDeviceId;

        if (!microphoneDeviceId) return;

        // Dispose any existing pipeline before rebuilding so reconfiguring on a
        // device/mic change doesn't orphan the previous worker or leave the old
        // microphone stream open. Only reached when the scribe socket is not live
        // (the setup effect no-ops while a recording/pause/finish is in flight),
        // so a hard teardown is correct here.
        get().teardown();

        let mediaStream: MediaStream;
        try {
          mediaStream = await navigator.mediaDevices.getUserMedia({
            audio: {
              deviceId: { exact: microphoneDeviceId },
              echoCancellation: false,
            },
          });
        } catch (error) {
          // A failed mic acquisition (device unplugged, permission revoked,
          // device busy) must surface, not vanish as an unhandled rejection.
          scribeClientLogger.error(
            "Failed to acquire microphone for scribe recording",
            { microphoneDeviceId, error },
          );
          set({ step: "errorMicrophoneInitial" });
          return;
        }

        if (!mediaStream) return;

        // Bail if a teardown ran while getUserMedia was in flight (rapid dep
        // churn can queue two setup() calls; the second's teardown fires before
        // the first's getUserMedia resolves — without this check the first would
        // write a dangling ScribeClient onto an already-torn-down pipeline).
        // Stop the just-acquired tracks first, otherwise the abandoned stream
        // keeps the microphone held open.
        if (isCancelled?.()) {
          mediaStream.getTracks().forEach((track) => {
            track.stop();
          });
          return;
        }

        const source = audioContext.createMediaStreamSource(mediaStream);
        const destination = audioContext.createMediaStreamDestination();

        source.connect(destination);

        const recorder = new MediaRecorder(destination.stream, {
          mimeType: "audio/wav",
        });

        const media = get().media;

        set({
          media: {
            ...media,
            mediaStream,
            audioContext,
            audioSource: source,
            audioDestination: destination,
            mediaRecorder: recorder,
          },
        });

        const client = createScribeClient(options, audioContext.sampleRate);

        set({ scribe: client });
      },
      teardown: (mode: "hard" | "graceful" = "hard") => {
        const state = get();
        const scribe = state.scribe;
        const { media } = state;

        // Stop feeding audio before disposing the client. mediaRecorder.stop()
        // fires its final 'dataavailable' event synchronously, so the last
        // chunk is already in the scribe client's buffer before close() starts
        // draining. teardown() is synchronous and cannot await close(), so this
        // fire-and-forget ordering is intentional — not a bug.
        if (media.mediaRecorder) media.mediaRecorder.stop();
        media.audioDestination?.disconnect();
        media.audioSource?.disconnect();
        media.mediaStream?.getTracks().forEach((track) => {
          track.stop();
        });

        if (scribe) {
          if (mode === "graceful") {
            // Flush buffered audio and let el8-transcribe finalize the session
            // instead of killing the worker. Listeners are intentionally left
            // attached so transcript items delivered during the flush still
            // reach their handlers. A failed flush must not leave the worker and
            // socket dangling silently — log it and fall back to a hard
            // terminate so we never reintroduce a silent teardown.
            // `scribingSessionId` is intentionally cleared AFTER close() resolves,
            // not synchronously here. Listeners are left attached during the flush
            // so transcript handlers can still read the id from the store — clearing
            // it immediately would cause them to throw on a null id and corrupt the
            // final step to errorNoRetry on a clean graceful close.
            void scribe
              .close()
              .then(() => {
                set({ scribingSessionId: undefined });
              })
              .catch((error) => {
                scribeClientLogger.error(
                  "Graceful scribe close failed; hard-terminating",
                  { scribingSessionId: state.scribingSessionId ?? null, error },
                );
                scribe.removeAllListeners();
                scribe.terminate();
                set({ scribingSessionId: undefined });
              });
          } else {
            scribe.removeAllListeners();
            scribe.terminate();
            // Hard teardown clears the id synchronously — there's no flush
            // window to keep it alive for, and a stale id would otherwise be
            // reused by getAuthToken on the next start.
            set({ scribingSessionId: undefined });
          }

          set({ scribe: null });
        }

        set({
          media: {
            ...media,
            mediaStream: null,
            audioSource: null,
            audioDestination: null,
            mediaRecorder: null,
          },
        });
      },
      setStep: (step) => {
        set({ step });
      },
      setLanguage: (language) => {
        set({ language });
      },
      setMicrophoneDeviceId: (microphoneDeviceId) => {
        set({ media: { ...get().media, microphoneDeviceId } });
      },
      getMediaDevices: async () => {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });

        stream.getTracks().forEach((track) => {
          track.stop();
        });

        const mediaDevices = await navigator.mediaDevices.enumerateDevices();

        const audioInputDevices = mediaDevices.filter((device) => {
          return (
            device.deviceId &&
            device.kind === "audioinput" &&
            device.deviceId !== "default"
          );
        });

        set({ media: { ...get().media, mediaDevices: audioInputDevices } });

        return audioInputDevices;
      },
      setupMicrophoneTest: async (microphoneDeviceId, isCancelled) => {
        get().teardown();

        let mediaStream: MediaStream;
        try {
          mediaStream = await navigator.mediaDevices.getUserMedia({
            audio: microphoneDeviceId
              ? {
                  deviceId: { exact: microphoneDeviceId },
                  echoCancellation: false,
                }
              : {
                  echoCancellation: false,
                },
          });
        } catch (error) {
          scribeClientLogger.error("Failed to acquire microphone for test", {
            microphoneDeviceId: microphoneDeviceId ?? null,
            error,
          });
          return null;
        }

        if (isCancelled?.()) {
          mediaStream.getTracks().forEach((track) => {
            track.stop();
          });
          return null;
        }

        const media = get().media;
        set({
          media: {
            ...media,
            mediaStream,
            microphoneDeviceId: microphoneDeviceId ?? media.microphoneDeviceId,
          },
        });

        return mediaStream;
      },
    }),
    {
      name: "scribe-store",
      storage: createJSONStorage(() =>
        IS_CHROME_EXTENSION ? chromeStorage : storage,
      ),
      partialize: (state) => ({
        language: state.language,
        media: {
          microphoneDeviceId: state.media.microphoneDeviceId,
          mediaDevices: [],
        },
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.media) {
          state.media.audioContext = createAudioContext();
        }
      },
    },
  ),
);

export function useMicrophoneDeviceId(): [
  microphoneDeviceId: string | null,
  setMicrophoneDeviceId: (microphoneDeviceId: string | null) => void,
] {
  const microphoneDeviceId = useScribeStore((s) => s.media.microphoneDeviceId);
  const setMicrophoneDeviceId = useScribeStore((s) => s.setMicrophoneDeviceId);

  return [microphoneDeviceId, setMicrophoneDeviceId];
}

export function useScribeLanguage(): [
  language: ScribeLanguage,
  setLanguage: (language: ScribeLanguage) => void,
] {
  const language = useScribeStore((s) => s.language);
  const setLanguage = useScribeStore((s) => s.setLanguage);

  return [language, setLanguage];
}

export function useMediaStream(): [mediaStream: MediaStream | null] {
  const mediaStream = useScribeStore((s) => s.media.mediaStream);

  return [mediaStream];
}

export function useStep(): [
  step: AiScribeStep,
  setStep: (step: AiScribeStep) => void,
] {
  const step = useScribeStore((s) => s.step);
  const setStep = useScribeStore((s) => s.setStep);

  return [step, setStep];
}

export function useOnStepChange(
  config: Partial<Record<AiScribeStep, () => void>>,
) {
  const step = useScribeStore((s) => s.step);
  const prevStepRef = usePrevious(step);

  // biome-ignore lint/correctness/useExhaustiveDependencies: prevStepRef is used in the effect
  useEffect(() => {
    if (prevStepRef !== step) {
      config[step]?.();
    }
  }, [step, prevStepRef]);
}

export function useMediaDevices(): [
  mediaDevices: MediaDeviceInfo[] | null,
  getMediaDevices: () => Promise<MediaDeviceInfo[]>,
] {
  const media = useScribeStore((s) => s.media);
  const getMediaDevices = useScribeStore((s) => s.getMediaDevices);

  return [media.mediaDevices, getMediaDevices];
}

export function useAudioContext(): [audioContext: AudioContext] {
  const media = useScribeStore((s) => s.media);

  return [media.audioContext];
}

export function useAudioRecordingComponents(
  onDataAvailable: (this: IMediaRecorder, event: IBlobEvent) => Promise<void>,
) {
  const media = useScribeStore((s) => s.media);

  useEffect(() => {
    if (media.mediaRecorder) {
      media.mediaRecorder.ondataavailable = onDataAvailable;
    }
  }, [media.mediaRecorder, onDataAvailable]);

  return {
    audioSource: media.audioSource,
    audioDestination: media.audioDestination,
    mediaRecorder: media.mediaRecorder,
  };
}

/**
 * Owns setup/teardown. Passes options into setup() so media and scribe client
 * are created in one flow.
 */
export function useSetupScribe(options: ScribeSetupOptions | null) {
  const setup = useScribeStore((s) => s.setup);
  const teardown = useScribeStore((s) => s.teardown);
  const media = useScribeStore((s) => s.media);
  const getMediaDevices = useScribeStore((s) => s.getMediaDevices);
  const microphoneDeviceId = media.microphoneDeviceId;
  const devices = media.mediaDevices;

  useEffectOnce(() => {
    getMediaDevices();
  });

  // (Re)configure the media + scribe pipeline when the microphone, device list,
  // or options change. While a recording is live this is a no-op: reconfiguring
  // tears the pipeline down and recreates the scribe client, which would kill
  // the worker mid-session (the socket closes with 1001 and audio is lost).
  // `step` is read fresh from the store so this effect only re-runs on input
  // changes, not on every step transition.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `step` is read via getState(); setup/teardown are stable store actions.
  useEffect(() => {
    const action = resolveScribePipelineAction({
      step: scribeStore.getState().step,
      microphoneDeviceId,
      hasDevices: Boolean(devices?.length),
      hasOptions: Boolean(options),
    });

    if (action === "setup" && options) {
      // Guard against overlapping async setup() calls. If the deps change twice
      // in quick succession, the second call's teardown() runs synchronously
      // before the first call's getUserMedia resolves. Without a cancel flag the
      // first call would then write set({ scribe: client }) onto an already-torn-
      // down pipeline, leaving a dangling ScribeClient. The flag lets setup()
      // detect that teardown has run and bail before writing to the store.
      let cancelled = false;
      setup(options, () => cancelled).catch(() => {
        // getUserMedia failure is already handled inside setup()
      });
      return () => {
        cancelled = true;
      };
    } else if (action === "teardown") {
      teardown();
    }
    // "noop": a recording is live — leave the pipeline untouched.
  }, [microphoneDeviceId, devices, options]);

  // Dispose only on unmount. A live recording is flushed gracefully; an idle
  // pipeline is hard-terminated. Because this runs only on unmount (empty deps),
  // a device/mic refresh or options change can no longer tear down an
  // in-progress recording.
  // biome-ignore lint/correctness/useExhaustiveDependencies: unmount-only cleanup; teardown is a stable store action and `step` is read via getState().
  useEffect(() => {
    return () => {
      const action = resolveScribeTeardownAction(scribeStore.getState().step);

      teardown(action === "graceful-close" ? "graceful" : "hard");
    };
    // After a graceful unmount, `step` is not reset — it stays in the live
    // state it had when the component unmounted (e.g. "recordingActive"). On
    // remount the setup effect reads `step` via getState() and returns "noop"
    // for any live step, so the pipeline isn't accidentally rebuilt for a
    // defunct recording.
    //
    // This is safe in practice because AIScribe is mounted at the patient-layout
    // level and does NOT remount on intra-patient navigation (PatientLayout is
    // the route layout component, not key'd on any dynamic value). If it ever
    // did remount, the startRecording catch handler already resets `step` to
    // "initial" on the first Start attempt, so the user self-heals on one press.
  }, []);
}

export function useTeardown(): () => void {
  const teardown = useScribeStore((s) => s.teardown);

  return teardown;
}

export function resetScribingSessionId(): void {
  scribeStore.getState().setScribingSessionId(undefined);
}

export function useScribeReady(): boolean {
  const media = useScribeStore((s) => s.media);
  const scribe = useScribeStore((s) => s.scribe);

  return isScribeReady({
    mediaDevices: media.mediaDevices,
    microphoneDeviceId: media.microphoneDeviceId,
    mediaRecorder: media.mediaRecorder,
    scribe,
  });
}

export const useScribeStore = scribeStore;
